import { useState } from 'react'
import * as DropdownMenu from '@radix-ui/react-dropdown-menu'
import cx from 'classnames'

import CheckboxComponent from 'common/components/checkbox'

import Footer from './Footer'

export const experimentType = {
  optimize: 'optimize',
  personalize: 'personalize',
}

interface Option {
  value: string
  label: string
  onClick: (value: string) => void
  isDisabled?: boolean
}

interface MenuProps {
  options: Option[]
  checkedItems: string[]
  onApply: () => void
  onClear: () => void
}

const CustomMenu = ({ options, checkedItems, onApply, onClear }: MenuProps) => (
  <div className="shadow-lg box-border py-2 z-40 w-75 bg-white border-1 border-coolGray-300">
    <div>
      {options.map((option) => (
        <div
          key={option.value}
          className={cx(
            'pt-2 px-4 pb-2 text-coolGray-800 font-medium',
            option.isDisabled
              ? 'cursor-not-allowed opacity-50'
              : 'hover:bg-coolGray-50 cursor-pointer'
          )}
        >
          <CheckboxComponent
            isChecked={checkedItems?.some((value) => value === option.value)}
            onChange={() => option.onClick(option.value)}
            className="mr-2 capitalize"
            label={option.label}
            isDisabled={option.isDisabled}
          />
        </div>
      ))}
    </div>
    <Footer onClear={onClear} onApply={onApply} />
  </div>
)

interface Props {
  animation?: string
  placement?: string
  overlayClassName?: string
  children: React.ReactNode
  getPopupContainer: ((node: HTMLElement) => HTMLElement) | undefined
  onApply: (items: string[]) => void
}

export const ExperimentMenu = ({ children, onApply }: Props) => {
  const [checkedItems, setCheckedItems] = useState<string[]>([])
  const [isOpened, setIsOpened] = useState(false)

  const onClickCheckbox = (value: string) => {
    if (checkedItems.some((currentValue) => currentValue === value)) {
      setCheckedItems((checkedItems) =>
        checkedItems.filter((item) => item !== value)
      )
    } else {
      setCheckedItems((checkedItems) => [...checkedItems, value])
    }
  }

  return (
    <DropdownMenu.Root open={isOpened} onOpenChange={setIsOpened}>
      <DropdownMenu.Trigger asChild>
        <span
          data-cy="tab-experiments"
          data-testid="tab-experiments"
          className="updated-component capitalize py-2"
        >
          {children}
        </span>
      </DropdownMenu.Trigger>
      <DropdownMenu.Portal>
        <DropdownMenu.Content
          side="bottom"
          align="start"
          alignOffset={-16}
          sideOffset={5}
        >
          <CustomMenu
            checkedItems={checkedItems}
            options={Object.keys(experimentType).map((option) => ({
              value: option,
              label: option,
              onClick: onClickCheckbox,
            }))}
            onClear={() => {
              setCheckedItems([])
              onApply([])
            }}
            onApply={() => {
              setIsOpened(false)
              onApply(checkedItems)
            }}
          />
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  )
}

export default ExperimentMenu
